import { useEffect, useCallback } from 'react';
import {
  portalAxios,
  performManualLogout,
  clearMicroFrontendTokens,
} from '../lib/axios';
import { clearSessionCookies, getLoginUrl } from '../lib/axios/auth-utils';
import { decodeJWT } from '../lib/axios/jwt-utils';
import { useSessionStore, updateSessionData } from '../stores/sessionStore';
import type {
  SessionData,
  Permission,
  Organization,
  TokenClaims,
  UseSessionReturn,
} from '@/utils/types';

// bridge para MFEs legados (MF 0.15.x) que não compartilham a store
const NSPORTAL_SESSION_KEY = '__NSPORTAL_SESSION__';

const login = () => {
  clearSessionCookies();
  const redirectUri = window.location.href;
  const loginUrl = getLoginUrl(redirectUri);
  window.location.href = loginUrl;
};

function transformToPermissions(
  resourceAccess: TokenClaims['resource_access']
): Permission[] {
  if (!resourceAccess) return [];

  return Object.entries(resourceAccess).map(([clientId, clientData]) => ({
    id: clientId,
    roles: Array.isArray(clientData.roles) ? clientData.roles : [],
  }));
}

function transformToOrganizations(
  organization: TokenClaims['organization']
): Organization[] {
  if (!organization) return [];

  return Object.entries(organization).map(([orgName, orgData]) => ({
    id: orgData.id,
    name: orgName,
  }));
}

function extractTokenData(accessToken: string): TokenClaims | null {
  try {
    const decoded = decodeJWT(accessToken);

    if (!decoded) return null;

    return {
      name: typeof decoded.name === 'string' ? decoded.name : undefined,
      email: typeof decoded.email === 'string' ? decoded.email : undefined,
      resource_access:
        typeof decoded.resource_access === 'object' &&
        decoded.resource_access !== null
          ? (decoded.resource_access as TokenClaims['resource_access'])
          : undefined,
      organization:
        typeof decoded.organization === 'object' &&
        decoded.organization !== null
          ? (decoded.organization as TokenClaims['organization'])
          : undefined,
    };
  } catch (error) {
    console.error('[NSPortal] Erro ao extrair dados do token:', error);
    return null;
  }
}

export async function validateSession(): Promise<SessionData> {
  const response = await portalAxios.get('/keystone/Authentication/session', {
    withCredentials: true,
  });

  if (response.status !== 200) {
    throw new Error('Não autenticado');
  }

  const basicData = response.data;
  const tokenClaims = extractTokenData(basicData.access_token);

  return {
    sub: basicData.sub,
    name: basicData.name,
    email: tokenClaims?.email,
    access_token: basicData.access_token,
    refresh_token: basicData.refresh_token,
    permissions: transformToPermissions(tokenClaims?.resource_access),
    organizations: transformToOrganizations(tokenClaims?.organization),
  };
}

const CACHE_TIME = 5 * 60 * 1000; // 5 minutos

// Garantia de limpeza dos tokens sempre que o nsApps é carregado.
// Isso evita que tokens de exchange antigos sejam usados em uma nova sessão.
let hasBootstrapCleared = false;

export const setNewUserSession = (sessionData: SessionData): void => {
  updateSessionData(sessionData);
};

export function useSession(): UseSessionReturn {
  const {
    data: sessionData,
    loading,
    error,
    isValid,
    timestamp,
    isLoggingOut,
    setSession,
    clearSession,
    setLoading,
    setError,
    setLoggingOut,
  } = useSessionStore();

  const refreshSession = useCallback(async () => {
    const startTime = Date.now();
    setLoading(true);
    setError(null);

    if (!hasBootstrapCleared) {
      hasBootstrapCleared = true;
      clearMicroFrontendTokens();
    }

    try {
      const data = await validateSession();
      setSession(data);
      (globalThis as Record<string, unknown>)[NSPORTAL_SESSION_KEY] = {
        data: {
          sub: data.sub,
          name: data.name,
          email: data.email,
          permissions: data.permissions,
          organizations: data.organizations,
        },
        loading: false,
        error: null,
      };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMsg);
      clearSession();
      (globalThis as Record<string, unknown>)[NSPORTAL_SESSION_KEY] = {
        data: null,
        loading: false,
        error: errorMsg,
      };
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 500 - elapsed);
      setTimeout(() => {
        setLoading(false);
      }, remaining);
    }
  }, [setSession, clearSession, setLoading, setError]);

  const logout = useCallback(() => {
    setLoggingOut(true);
    clearSession();
    performManualLogout();
  }, [clearSession, setLoggingOut]);

  useEffect(() => {
    if (isLoggingOut) return;

    const now = Date.now();
    const cacheAge = now - timestamp;

    if (isValid && cacheAge < CACHE_TIME && sessionData) {
      return;
    }

    refreshSession();
  }, [refreshSession, sessionData, timestamp, isLoggingOut, isValid]);

  useEffect(() => {
    if (error && !loading && !sessionData) {
      login();
    }
  }, [error, loading, sessionData]);

  return {
    isAuthenticated: !!sessionData && !error,
    sessionData,
    loading,
    error,
    logout,
    refreshSession,
    isLoggingOut,
  };
}
